import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
	CommodityTitle,
	CommodityWrapper,
	DateRangeWrapper,
	FormSummary,
	FormSummaryLabel,
	FormSummaryLine,
	FormSummaryValue,
	Grid,
	// NoteWrapper,
	ScrollContainer,
	SectionContent,
	SectionHeader,
	SectionWrapper,
	SKUTitle,
	SKUWrapper,
	Title,
	Wrapper,
} from './DownloadDeck.styles';
import MonthPicker from 'Settings/FirstUsageSettings/MonthPicker';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import {
	getDateFormat,
	getDateRangeMonthYearSrc,
	getDateRangeMonthYearState,
	getMinMaxDatesFromData,
} from 'utils/date';
import {
	ApButtonMain,
	ApButtonTab,
	ApLoaderDefault,
	ApLoaderText,
	ApSimpleDropdown,
} from '@alixpartners/ui-components';
import { getDirectIndirectDates, getSKUList } from 'utils/apiRequests';
import { useToastMessageContext } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { NotificationTypes } from 'components/Toast/constants';
import { isNull } from 'lodash';
import { downloadReport, getDeck } from '../../utils/apiRequests';
import DownloadProgressBar from 'Data/DownloadData/DownloadProgressBar';
import { useFetchDataRangesContextProvider } from 'contexts/DownloadDeckContext/DateRangeContext';

const DownloadDeck = () => {
	const { showToast } = useToastMessageContext();
	const { options } = useContext(SettingsOptionsContext);
	const { dateRanges } = options;
	const { minDate, maxDate } = getMinMaxDatesFromData(dateRanges);
	const [wholeRange, setWholeRange] = useState(
		getDateRangeMonthYearSrc(minDate, maxDate)
	);
	const [indirectWholeRange, setIndirectWholeRange] = useState(
		getDateRangeMonthYearSrc(minDate, maxDate)
	);

	const [skuData, setSkuData] = useState([
		{ sku: '', commodity: '' },
		{ sku: '', commodity: '' },
	]);
	const [directDateRange, setDirectDateRange] = useState({
		srcRange: {},
		stateRange: {},
		dateFrom: '',
		dateTo: '',
	});
	const [inDirectDateRange, setInDirectDateRange] = useState({
		srcRange: {},
		stateRange: {},
		dateFrom: '',
		dateTo: '',
	});

	useEffect(
		() => {
			setDirectDateRange(prevState => ({
				...prevState,
				srcRange: wholeRange,
			}));
		},
		[wholeRange]
	);
	useEffect(
		() => {
			setInDirectDateRange(prevState => ({
				...prevState,
				srcRange: indirectWholeRange,
			}));
		},
		[indirectWholeRange]
	);

	const [skuList, setSkuList] = useState([]);
	const [commodityList, setCommodityList] = useState([]);
	const [isFetchingDates, setIsFetchingDates] = useState(true);
	const [isSKUFetching, setIsSKUFetching] = useState(false);
	const [isNotificationShown, setIsNotificationShown] = useState(false);
	const [hasExportError, setHasExportError] = useState(false);

	const {
		fetchDateRanges,
		setFetchDateRanges,
	} = useFetchDataRangesContextProvider();

	const setValueInContext = newValue => {
		setFetchDateRanges(newValue);
	};
	const handleFetchDateRanges = async () => {
		try {
			const response = await getDirectIndirectDates();
			if (response.succeeded) {
				const { indirect, direct } = response?.data;

				const last12MonthsDatesInDirect = getDateRangeMonthYearState(
					indirect[0].min_date,
					indirect[0].max_date
				);
				setIndirectWholeRange(
					getDateRangeMonthYearSrc(indirect[0].min_date, indirect[0].max_date)
				);
				if (isNull(indirect[0].min_date) || isNull(indirect[0].max_date)) {
					setInDirectDateRange(prevState => ({
						...prevState,
						stateRange: getDateRangeMonthYearState(minDate, maxDate),
					}));
				} else {
					setInDirectDateRange(prevState => ({
						...prevState,
						stateRange: last12MonthsDatesInDirect,
						dateFrom: indirect[0].min_date,
						dateTo: indirect[0].max_date,
					}));
				}

				const last12MonthsDatesDirect = getDateRangeMonthYearState(
					direct[0].min_date,
					direct[0].max_date
				);
				setWholeRange(
					getDateRangeMonthYearSrc(direct[0].min_date, direct[0].max_date)
				);
				if (isNull(direct[0].min_date) || isNull(direct[0].max_date)) {
					setDirectDateRange(prevState => ({
						...prevState,
						stateRange: getDateRangeMonthYearState(minDate, maxDate),
						dateFrom: indirect[0].min_date,
						dateTo: indirect[0].max_date,
					}));
				} else {
					setDirectDateRange(prevState => ({
						...prevState,
						stateRange: last12MonthsDatesDirect,
						dateFrom: direct[0].min_date,
						dateTo: direct[0].max_date,
					}));
				}
			} else {
				showToast(
					'Failed to get dates. Try again later.',
					NotificationTypes.danger,
					3000
				);
			}
		} catch (error) {
			showToast(
				'Failed to get dates. Try again later.',
				NotificationTypes.danger,
				3000
			);
			console.error('Error fetching data:', error);
		} finally {
			setIsFetchingDates(false);
		}
	};

	useEffect(
		() => {
			handleFetchDateRanges();
		}, // eslint-disable-next-line
		[maxDate, minDate]
	);

	useEffect(
		() => {
			if (fetchDateRanges !== null) {
				handleFetchDateRanges();
				setValueInContext(null);
			}
		}, // eslint-disable-next-line
		[fetchDateRanges]
	);

	useEffect(
		() => {
			const fetchSKUList = async () => {
				if (directDateRange.dateFrom && directDateRange.dateTo) {
					setIsSKUFetching(true);
					try {
						const payload = {
							dateFrom: directDateRange.dateFrom,
							dateTo: directDateRange.dateTo,
						};
						const response = await getSKUList(payload);
						if (response.succeeded) {
							const { sku, commodity } = response.data;
							if (sku.length === 0) {
								showToast(
									'No SKU available for selected Direct date range.',
									NotificationTypes.simple,
									5000
								);
							} else {
								setSkuList(
									sku.map(x => {
										return { label: x, value: x };
									})
								);
								setCommodityList(
									commodity.map(x => {
										return { label: x, value: x };
									})
								);
								showToast(
									'SKU is now available.',
									NotificationTypes.simple,
									4000
								);
							}
						} else {
						}
					} catch (error) {
						showToast(
							'Failed to get SKU & Commodity. Try again later.',
							NotificationTypes.danger,
							3000
						);
						console.error('Error fetching data:', error);
					} finally {
						setIsSKUFetching(false);
					}
				}
			};

			fetchSKUList();
		}, // eslint-disable-next-line
		[directDateRange.dateFrom, directDateRange.dateTo]
	);

	const handleSKUAndCommodityChange = (index, field, value) => {
		setSkuData(prevItems => {
			const updatedItems = [...prevItems];
			updatedItems[index][field] = value.value;

			return updatedItems;
		});
	};

	const handleDirectDateRangeChange = value => {
		const minMaxDates = getDateFormat(value);
		setDirectDateRange(prevState => ({
			...prevState,
			stateRange: value,
			...minMaxDates,
		}));
	};

	const handleInDirectDateRangeChange = value => {
		const minMaxDates = getDateFormat(value);
		setInDirectDateRange(prevState => ({
			...prevState,
			stateRange: value,
			...minMaxDates,
		}));
	};

	const closeNotification = () => {
		setIsNotificationShown(false);
		setHasExportError(false);
	};

	const onDownloadHandler = () => {
		const payload = {
			directDates: {
				dateFrom: directDateRange.dateFrom,
				dateTo: directDateRange.dateTo,
			},
			indirectDates: {
				dateFrom: inDirectDateRange.dateFrom,
				dateTo: inDirectDateRange.dateTo,
			},
			skuData: skuData,
		};

		try {
			getDeck(payload);
			setIsNotificationShown(true);
		} catch (e) {
			setIsNotificationShown(true);
			setHasExportError(true);
		}

		setTimeout(() => {
			closeNotification();
		}, 5000);
	};

	const handleDownloadReport = type => {
		const payload = {
			type: type,
		};

		try {
			downloadReport(payload);
			setIsNotificationShown(true);
		} catch (e) {
			setIsNotificationShown(true);
			setHasExportError(true);
		}

		setTimeout(() => {
			closeNotification();
		}, 5000);
	};

	const hasEmptyValues = useMemo(
		() => {
			return skuData.some(item => !item.sku || !item.commodity);
		},
		[skuData]
	);

	const selectedSkus = skuData.map(item => item.sku).filter(sku => sku);
	const availableSkus = skuList.filter(sku => !selectedSkus.includes(sku));

	return (
		<Wrapper>
			<ScrollContainer>
				<Grid>
					<>
						{isFetchingDates ? (
							<SectionWrapper>
								<ApLoaderDefault loaderText={() => <ApLoaderText />} />
							</SectionWrapper>
						) : (
							<SectionWrapper>
								<SectionHeader>
									<Title>Download Deck</Title>
								</SectionHeader>
								<SectionContent>
									<FormSummary>
										<FormSummaryLine>
											<FormSummaryLabel>
												Date Range for Direct Spend:
											</FormSummaryLabel>
											<FormSummaryValue>
												<DateRangeWrapper>
													<MonthPicker
														srcRange={directDateRange.srcRange}
														stateRange={directDateRange.stateRange}
														onConfirm={handleDirectDateRangeChange}
													/>
												</DateRangeWrapper>
											</FormSummaryValue>
										</FormSummaryLine>
										<FormSummaryLine>
											<FormSummaryLabel>
												Date Range for Indirect Spend:
											</FormSummaryLabel>
											<FormSummaryValue>
												<DateRangeWrapper>
													<MonthPicker
														srcRange={inDirectDateRange.srcRange}
														stateRange={inDirectDateRange.stateRange}
														onConfirm={handleInDirectDateRangeChange}
													/>
												</DateRangeWrapper>
											</FormSummaryValue>
										</FormSummaryLine>
										{skuData.map((item, index) => (
											<FormSummaryLine
												key={index}
												style={{ marginBottom: '20px' }}
											>
												<FormSummaryLabel>SKU {index + 1}:</FormSummaryLabel>
												<FormSummaryValue>
													<div className="options_wrapper">
														<SKUWrapper>
															<div className="dropdown_selection">
																<ApSimpleDropdown
																	onChange={v =>
																		handleSKUAndCommodityChange(index, 'sku', v)
																	}
																	options={availableSkus}
																	selected={selectedSkus}
																	label="Select SKU"
																	disabled={isSKUFetching}
																/>
															</div>
															<SKUTitle>
																{item.sku === '' ? '' : item.sku}
															</SKUTitle>
														</SKUWrapper>

														<CommodityWrapper>
															<div className="dropdown_selection">
																<ApSimpleDropdown
																	onChange={v =>
																		handleSKUAndCommodityChange(
																			index,
																			'commodity',
																			v
																		)
																	}
																	options={commodityList}
																	selected={
																		commodityList.filter(
																			x => x.value === item.commodity
																		)?.[0]
																	}
																	label="Select Commodity"
																	disabled={isSKUFetching}
																/>
															</div>
															<CommodityTitle>
																{item.commodity === '' ? '' : item.commodity}
															</CommodityTitle>
														</CommodityWrapper>
													</div>
												</FormSummaryValue>
											</FormSummaryLine>
										))}
									</FormSummary>
									<ApButtonMain
										id="downloadButton"
										onClick={onDownloadHandler}
										disabled={hasEmptyValues}
									>
										Download
									</ApButtonMain>
									{/* <NoteWrapper>
										Note: Please download the file within 40 minutes after
										receiving the notification.
									</NoteWrapper> */}
								</SectionContent>
							</SectionWrapper>
						)}
					</>

					<SectionWrapper>
						<SectionHeader>
							<Title>Download Reports</Title>
						</SectionHeader>
						<SectionContent>
							<div
								style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}
							>
								<div>
									<ApButtonMain
										onClick={() => {
											handleDownloadReport(1);
										}}
									>
										Single & Multi Sourced SKU's
									</ApButtonMain>
								</div>
								<div>
									<ApButtonMain
										onClick={() => {
											handleDownloadReport(2);
										}}
									>
										Multi sourced SKU's Unit Pricing
									</ApButtonMain>
								</div>
								<div>
									<ApButtonMain
										onClick={() => {
											handleDownloadReport(3);
										}}
									>
										SKU & Commodity Price Comparison
									</ApButtonMain>
								</div>
							</div>
						</SectionContent>
					</SectionWrapper>
				</Grid>
			</ScrollContainer>
			<ApButtonTab />
			{isNotificationShown && (
				<DownloadProgressBar
					error={hasExportError}
					successMessage="Generating file... You will receive a notification when the file has been generated"
					onClose={closeNotification}
				/>
			)}
		</Wrapper>
	);
};

export default DownloadDeck;
